import React, {
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { defineMessages } from "react-intl";

import UserService, { IDP_TYPE, STAGE, UppUser } from "@app/service/user";
import {
  FetchResult,
  isApolloError,
  useLazyQuery,
  useMutation,
} from "@apollo/client";
import {
  IntlContext,
  Language,
  NotificationContext,
  UppAuthContext,
} from "@app/provider";
import { ResultCodes } from "@app/service/resultCodes";
import { AddNotificationParameters } from "@app/provider/notification";
import { getExtendedServerErrorNotificationParams } from "@app/utils/getExtendedServerErrorNotificationParams";
import {
  getGraphQLErrorBodies,
  getGraphQLErrorCode,
} from "@app/provider/error/graphQLErrorCode";
import { updatePostbillLocalStorage } from "@app/postBill/utils/localStorage";
import { updateUpsLocalStorage } from "@app/ups/utils/localStorage";
import { RecaptchaTokens } from "@app/hooks";

export interface User {
  id?: string;
  patientUID?: string;
  stage?: STAGE;
  email?: string;
}

export enum LoginStatus {
  FAIL = "FAIL",
  SUCCESS = "SUCCESS",
}

interface newUserDTO {
  oktaUID: string;
  email: string;
  invite: string | null;
}

export interface UpdateUserDTO {
  language?: Language;
  askForMfa?: boolean;
  stage?: STAGE;
}

export interface UpdateUserLanguageDTO {
  email: string | null;
  language: Language;
}

interface ErrorResponse {
  message: string;
}

export interface ErrorBody {
  code: string;
  message: string;
}

export interface UpdateEmailData {
  newEmail: string;
}

export interface LogUserLoginResponse {
  attemptsLeft?: number;
  isAccountActivated?: boolean;
}

interface ChangePasswordDTO {
  oldPassword: string;
  newPassword: string;
}

export interface ChangePasswordData {
  success: boolean;
}

interface UnlockUserData {
  unlockedUser?: { success: boolean; email: string };
  unlockError?: string;
}

export interface AddPatientAcknowledgementDTO {
  email: string;
  accepted: boolean;
  acknowledgementText: string;
  consentTimestamp: string;
}

interface AddPatientAcknowledgementResponse {
  email: string;
  accepted: boolean;
}

export interface UserController {
  isLoading: boolean;
  uppUser: UppUser | undefined;
  uppUserError: ErrorBody[];
  showError: (err?: ErrorResponse) => void;
  createUser: (newUser: newUserDTO) => Promise<void>;
  createUserError?: string;
  resetCreateUserError: () => void;
  loadProfile: () => Promise<UppUser | undefined>;
  updateUser: (userData: UpdateUserDTO) => Promise<void>;
  updatedUser?: UppUser | null;
  updateUserLanguageNonAuth: (
    userLanguageData: UpdateUserLanguageDTO
  ) => Promise<void>;
  updateEmail: (newEmail: string) => Promise<UpdateEmailData | undefined>;
  updateEmailData?: UpdateEmailData;
  updateEmailError?: ErrorBody;
  logUserLogin: (props: {
    email: string;
    status: LoginStatus;
    token?: string | null;
    idpType?: IDP_TYPE;
  }) => Promise<LogUserLoginResponse | undefined>;
  addUserToPasswordlessGroup: (email: string) => Promise<void>;
  removeUserFromPasswordlessGroup: (email: string) => Promise<void>;
  handleUserLogin: () => Promise<void>;
  unlockUser: (
    email: string,
    dateOfBirth?: string,
    lastName?: string,
    tokens?: RecaptchaTokens
  ) => Promise<FetchResult<{ success: boolean; email: string }>>;
  unlockedUser?: { success: boolean; email: string };
  changePassword: (
    props: ChangePasswordDTO
  ) => Promise<ChangePasswordData | undefined>;
  changePasswordError?: ResultCodes;
  resetChangePasswordError: () => void;
  deleteUser: (
    email: string
  ) => Promise<FetchResult<{ success: boolean; email: string }>>;
  deletedUserData?: { success: boolean; email: string };
  unlockError?: string;
  canEdit?: boolean;
  clearUnlockUserData: () => void;
  addPatientAcknowledgement: (
    acknowledgementData: AddPatientAcknowledgementDTO
  ) => Promise<void>;
  addPatientAcknowledgementLoading?: boolean;
  validatePhoneNumber: (
    phone: string
  ) => Promise<{ isValid: boolean } | undefined>;
  getUserIdpType: () => Promise<IDP_TYPE | null>;
}

const ignoreRejection = <T,>(promise: Promise<T>): Promise<void> =>
  promise.then(() => undefined).catch(() => undefined);

export const Context = React.createContext<UserController>({
  isLoading: false,
  uppUser: undefined,
  uppUserError: [],
  unlockedUser: undefined,
  deletedUserData: undefined,
  unlockError: undefined,
  canEdit: undefined,
  showError: () => undefined,
  createUser: async () => undefined,
  resetCreateUserError: () => undefined,
  loadProfile: async () => undefined,
  updateUser: async () => undefined,
  updateUserLanguageNonAuth: () => Promise.reject(),
  updateEmail: async () => undefined,
  logUserLogin: async () => undefined,
  addUserToPasswordlessGroup: async () => undefined,
  removeUserFromPasswordlessGroup: async () => undefined,
  handleUserLogin: async () => undefined,
  unlockUser: async () => ({}),
  changePassword: async () => undefined,
  resetChangePasswordError: () => undefined,
  deleteUser: async () => ({}),
  clearUnlockUserData: () => undefined,
  addPatientAcknowledgement: () => Promise.reject(),
  validatePhoneNumber: async () => undefined,
  getUserIdpType: () => Promise.reject(),
});

const messages = defineMessages({
  userFactorIsAlreadySetup: {
    id: "userFactorIsAlreadySetup",
    defaultMessage: "Factor is already set up",
  },
});

Context.displayName = "UserContext";

const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [profileErrors, setProfileErrors] = useState<ErrorBody[]>([]);
  const [unlockUserData, setUnlockUserData] = useState<UnlockUserData>({});
  const { addNotification, clear } = useContext(NotificationContext);
  const { currentLanguage, changeLanguage } = useContext(IntlContext);
  const { setProfile } = useContext(UppAuthContext);

  const showError = (err?: Error) => {
    if (err && isApolloError(err) && err.graphQLErrors?.length > 0) {
      const code = getGraphQLErrorCode(err);
      switch (code) {
        case ResultCodes.FACTOR_IS_ALREADY_SETUP:
          addNotification({
            message: messages.userFactorIsAlreadySetup.defaultMessage,
            type: "error",
          });
          break;
        default: {
          const notificationParams: AddNotificationParameters =
            getExtendedServerErrorNotificationParams(err);
          addNotification(notificationParams);
        }
      }
    } else {
      addNotification({
        type: "error",
      });
    }
  };

  const [
    createUser,
    {
      loading: createUserIsLoading,
      error: createUserError,
      reset: resetCreateUserError,
    },
  ] = useMutation<{
    createUser: UppUser;
  }>(UserService.createUser(), {
    update: (cache, { data }) => {
      const { createUser } = data || {};
      const { profile } =
        cache.readQuery<{ profile: UppUser }>({
          query: UserService.loadProfile(),
        }) || {};
      cache.writeQuery({
        query: UserService.loadProfile(),
        data: { profile: { ...profile, ...createUser } },
      });
    },
  });

  const [loadProfile, { loading: profileIsLoading, data: loadProfileData }] =
    useLazyQuery<{ profile: UppUser }>(UserService.loadProfile(), {
      fetchPolicy: "network-only",
    });

  const [updateUser, { loading: updateUserIsLoading, data: updateUserData }] =
    useMutation<{ updateUser: UppUser }>(UserService.updateUser(), {
      onError: (error) => {
        console.error(error);
        const notificationParams: AddNotificationParameters =
          getExtendedServerErrorNotificationParams(error);
        addNotification(notificationParams);
      },
    });

  const [updateUserLanguage] = useMutation<{
    updateUserLanguage: { success: boolean };
  }>(UserService.updateUserLanguageNonAuth(), {
    onError: (error) => {
      const notificationParams: AddNotificationParameters =
        getExtendedServerErrorNotificationParams(error);
      addNotification(notificationParams);
    },
  });

  const [logUserLogin, { loading: isLogUserLoginLoading }] = useMutation<{
    logUserLogin: LogUserLoginResponse;
  }>(UserService.LogUserLogin());

  const [addUserToPasswordlessGroupMutation] = useMutation<{
    addUserToPasswordlessGroup: { success: boolean };
  }>(UserService.AddUserToPasswordlessGroup());

  const [removeUserFromPasswordlessGroupMutation] = useMutation<{
    removeUserFromPasswordlessGroup: { success: boolean };
  }>(UserService.RemoveUserFromPasswordlessGroup());

  const [
    updateEmail,
    {
      loading: updateEmailIsLoading,
      data: updateEmailData,
      error: updateEmailError,
    },
  ] = useMutation<{ updateEmail: UpdateEmailData }>(UserService.UpdateEmail(), {
    fetchPolicy: "no-cache",
  });

  const [unlockUser, { loading: unlockLoader }] = useLazyQuery(
    UserService.UnlockUser(),
    {
      fetchPolicy: "cache-and-network",
    }
  );

  const [
    changePassword,
    { loading: changePasswordIsLoading, error: changePasswordError, reset },
  ] = useMutation<{ changePassword: { success: boolean } }>(
    UserService.ChangePassword()
  );

  const [
    deleteUser,
    {
      error: deleteUserError,
      data: deletedUserData,
      loading: deleteUserLoader,
    },
  ] = useMutation(UserService.DeleteUser());

  const [getUserIdpType] = useLazyQuery(UserService.getUserIdpType(), {
    fetchPolicy: "no-cache",
  });

  const [validatePhoneNumber, { loading: validatePhoneNumberLoading }] =
    useLazyQuery<{
      validatePhoneNumber: { isValid: boolean };
    }>(UserService.validatePhoneNumber(), {
      fetchPolicy: "no-cache",
    });

  const [
    addPatientAcknowledgement,
    { loading: addPatientAcknowledgementLoading },
  ] = useMutation<{
    addPatientAcknowledgement: AddPatientAcknowledgementResponse;
  }>(UserService.AddPatientAcknowledgement());

  const handleProfileLoaded = (profile: UppUser | undefined) => {
    if (profile) {
      updatePostbillLocalStorage("authType", null);
      updateUpsLocalStorage("authType", null);
      setProfile(profile);

      if (profile.language) {
        changeLanguage(profile.language);
      }
    }
  };

  useEffect(() => {
    if (deleteUserError) {
      showError(deleteUserError);
    }
  }, [deleteUserError]);

  const handleLoadProfile = async () => {
    try {
      setProfileErrors([]);
      const response = await loadProfile();
      if (response.error) {
        throw response.error;
      }
      const profile = response.data?.profile;

      handleProfileLoaded(profile);
      return profile;
    } catch (error) {
      if (isApolloError(error) && error.graphQLErrors?.length > 0) {
        setProfileErrors(getGraphQLErrorBodies(error));
      } else {
        showError(error);
      }
    }
    return;
  };

  const handleUserCreate = async (newUser: newUserDTO) => {
    clear();
    setProfileErrors([]);
    await createUser({
      variables: {
        newUserData: newUser,
      },
    });
  };

  const handleUpdateUser = async (updateUserData: UpdateUserDTO) => {
    await updateUser({ variables: { updateUserData } }).then((res) => {
      if (res.data) {
        setProfile(res.data.updateUser);
      }
    });
  };

  const handleUpdateUserLanguageNonAuth = async (
    updateUserLanguageData: UpdateUserLanguageDTO
  ) => {
    await updateUserLanguage({
      variables: { updateUserLanguageData },
    });
  };

  const handleUpdateEmail = async (newEmail: string) => {
    const response = await updateEmail({ variables: { newEmail } });
    return response.data?.updateEmail;
  };

  const handleUserLoginTry = async ({
    email,
    status,
    token,
    idpType = IDP_TYPE.EMAIL,
  }: {
    email: string;
    status: LoginStatus;
    token?: string;
    idpType?: IDP_TYPE;
  }) => {
    try {
      const userLoginLog = await logUserLogin({
        variables: {
          userLoginData: {
            email,
            status,
            token,
            idpType,
          },
        },
      });

      return userLoginLog.data?.logUserLogin;
    } catch (error) {
      const errorCode = getGraphQLErrorCode(error);
      if (!errorCode || errorCode !== ResultCodes.USER_NOT_FOUND_ERROR) {
        throw error;
      }
    }
  };

  const handleAddUserToPasswordlessGroup = (email: string) =>
    ignoreRejection(
      addUserToPasswordlessGroupMutation({ variables: { email } })
    );

  const handleRemoveUserFromPasswordlessGroup = (email: string) =>
    ignoreRejection(
      removeUserFromPasswordlessGroupMutation({ variables: { email } })
    );

  const handleUserLogin = async () => {
    try {
      await handleUpdateUser({
        language: currentLanguage,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const clearUnlockUserData = () => {
    setUnlockUserData({});
  };

  const handleUnlockUser = async (
    email: string,
    dateOfBirth?: string,
    lastName?: string,
    tokens?: RecaptchaTokens
  ) => {
    clear();

    const response = await unlockUser({
      variables: {
        unlockUser: {
          email,
          dateOfBirth,
          lastName,
          recaptchaToken: tokens?.recaptchaToken,
          recaptchaV2Token: tokens?.recaptchaV2Token,
        },
      },
    });

    if (response.error) {
      setUnlockUserData({
        unlockError: getGraphQLErrorCode(response.error),
      });
      throw response.error;
    }

    if (response && response.data) {
      setUnlockUserData({
        unlockedUser: { email, ...response.data.unlockUser },
      });

      return { data: { email, ...response.data.unlockUser } };
    }

    setUnlockUserData({ unlockError: "Unlock can't be performed" });
    throw new Error("Unlock can't be performed");
  };

  const handleChangePassword = async ({
    oldPassword,
    newPassword,
  }: ChangePasswordDTO) => {
    const response = await changePassword({
      variables: { props: { oldPassword, newPassword } },
    });

    return response.data?.changePassword;
  };

  const handleDeleteUser = async (email: string) => {
    const response = await deleteUser({
      variables: {
        deleteUser: { email },
      },
    });

    if (response && response.data) {
      return { email, ...response.data.deleteUser };
    }
    throw Error();
  };

  const canEdit = loadProfileData?.profile?.idpType === IDP_TYPE.EMAIL;

  const handleAddPatientAcknowledgement = async (
    acknowledgementData: AddPatientAcknowledgementDTO
  ) => {
    await addPatientAcknowledgement({
      variables: {
        acknowledgementData: {
          email: acknowledgementData.email,
          accepted: acknowledgementData.accepted,
          acknowledgementText: acknowledgementData.acknowledgementText,
          consentTimestamp: acknowledgementData.consentTimestamp,
        },
      },
    });
  };

  const handleValidatePhoneNumber = async (
    phone: string
  ): Promise<{ isValid: boolean } | undefined> => {
    const result = await validatePhoneNumber({ variables: { phone } });

    return result.data?.validatePhoneNumber;
  };

  const handleGetUserIdpType = async (): Promise<IDP_TYPE | null> => {
    try {
      const response = await getUserIdpType();

      return response.data?.getUserIdpType ?? null;
    } catch (error) {
      console.error("Failed to fetch user idpType", error);
      return null;
    }
  };

  const isLoading =
    profileIsLoading ||
    createUserIsLoading ||
    updateUserIsLoading ||
    updateEmailIsLoading ||
    isLogUserLoginLoading ||
    unlockLoader ||
    changePasswordIsLoading ||
    deleteUserLoader ||
    validatePhoneNumberLoading;

  const userController: UserController = useMemo(
    () => ({
      isLoading,
      uppUser: loadProfileData?.profile,
      uppUserError: profileErrors,
      updatedUser: updateUserData?.updateUser,
      unlockedUser: unlockUserData?.unlockedUser,
      deletedUserData: deletedUserData?.deleteUser,
      showError,
      loadProfile: handleLoadProfile,
      createUser: handleUserCreate,
      createUserError: getGraphQLErrorCode(createUserError),
      resetCreateUserError,
      updateUser: handleUpdateUser,
      updateUserLanguageNonAuth: handleUpdateUserLanguageNonAuth,
      updateEmail: handleUpdateEmail,
      updateEmailData: updateEmailData?.updateEmail,
      updateEmailError: getGraphQLErrorBodies(updateEmailError)[0],
      logUserLogin: handleUserLoginTry,
      addUserToPasswordlessGroup: handleAddUserToPasswordlessGroup,
      removeUserFromPasswordlessGroup: handleRemoveUserFromPasswordlessGroup,
      handleUserLogin,
      unlockUser: handleUnlockUser,
      changePassword: handleChangePassword,
      changePasswordError: getGraphQLErrorCode(
        changePasswordError
      ) as ResultCodes,
      resetChangePasswordError: reset,
      deleteUser: handleDeleteUser,
      unlockError: unlockUserData?.unlockError,
      canEdit,
      clearUnlockUserData,
      addPatientAcknowledgement: handleAddPatientAcknowledgement,
      addPatientAcknowledgementLoading,
      validatePhoneNumber: handleValidatePhoneNumber,
      getUserIdpType: handleGetUserIdpType,
    }),
    [
      isLoading,
      loadProfileData,
      profileErrors,
      updateUserData,
      unlockUserData,
      deletedUserData,
      showError,
      handleLoadProfile,
      handleUserCreate,
      createUserError,
      resetCreateUserError,
      handleUpdateUser,
      handleUpdateUserLanguageNonAuth,
      handleUpdateEmail,
      updateEmailData,
      updateEmailError,
      handleUserLoginTry,
      handleUserLogin,
      handleUnlockUser,
      handleChangePassword,
      changePasswordError,
      reset,
      handleDeleteUser,
      canEdit,
      clearUnlockUserData,
      handleAddPatientAcknowledgement,
      addPatientAcknowledgementLoading,
      handleValidatePhoneNumber,
      getUserIdpType,
    ]
  );

  return <Context.Provider value={userController}>{children}</Context.Provider>;
};

export default UserProvider;
